/**
 * A reusable form input component integrated with React Hook Form.
 * Props:
 * - label: the input label text
 * - name: the field name (used for registration)
 * - required: adds red asterisk to label when required
 * - register: the `register` function from useForm()
 * - errors: the `errors` object from useForm().formState
 * - type: input type (e.g., 'text', 'email', 'number'); defaults to 'text'
 * - options: to be used with select / radio / checkbox
 * - ...rest: any other input props (placeholder, disabled, etc.)
 */
const Input = ({
  label,
  name,
  register,
  errors,
  required = false,
  type = "text",
  className = "",
  options = [],
  ...rest
}) => {
  return (
    <div>
      {label && (
        <label htmlFor={name} className="block mb-1 text-sm md:text-base">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {type === "select" ? (
        <select
          id={name}
          className={`w-full border border-primary rounded-md px-3 py-2 bg-secondary ${className}`}
          {...register(name)}
          {...rest}
        >
          <option value="">Select an option</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : type === "radio" ? (
        <div className={`flex flex-col gap-2 ${className}`}>
          {options.map((option) => (
            <label key={option.value} className="flex items-center gap-2">
              <input
                type="radio"
                value={option.value}
                className="accent-indigo-500"
                {...register(name)}
              />
              {option.label}
            </label>
          ))}
        </div>
      ) : type === "checkbox" ? (
        <div className={`flex flex-col gap-2 ${className}`}>
          {options.map((option) => (
            <label key={option.value} className="flex items-center gap-2">
              <input
                type="checkbox"
                value={option.value}
                className="accent-indigo-500"
                {...register(name)}
              />
              {option.label}
            </label>
          ))}
        </div>
      ) : (
        // type === 'text'
        <input
          id={name}
          type={type}
          className={`
            block text-sm md:text-base w-full border rounded-lg border-primary px-3 py-2 bg-secondary focus:outline-none focus:ring-2 focus:ring-indigo-500 
            ${className}
            `}
          aria-invalid={errors[name] ? "true" : "false"}
          {...register(name)}
          {...rest}
        />
      )}

      {errors[name] && (
        <p className="mt-1 text-red-600">{errors[name].message}</p>
      )}
    </div>
  );
};

export default Input;
